// src/store/api/contactApi.ts
import { baseApi } from "./baseApi";
import type {
  CreateContactDto,
  UpdateContactDto,
  ContactResponseDto,
  ContactFilterDto,
  ContactStatsDto,
  PaginatedContactResponseDto,
  BulkUpdateContactStatusDto,
  BulkContactOperationResponseDto,
  ContactStatus,
} from "../../types/contact";

export const contactApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create Contact
    createContact: builder.mutation<ContactResponseDto, CreateContactDto>({
      query: (data) => ({
        url: "/contacts",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Contact"],
    }),

    // Get Contacts with filters and pagination
    getContacts: builder.query<PaginatedContactResponseDto, ContactFilterDto>({
      query: (params) => ({
        url: "/contacts",
        params,
      }),
      transformResponse: (response: any) => {
        // Handle the API response wrapper format
        if (response.success && response.data) {
          return response.data;
        }
        return response;
      },
      providesTags: (result) =>
        result?.data?.length
          ? [
              ...result.data.map(({id}) => ({type: "Contact" as const, id})),
              {type: "Contact", id: "LIST"},
            ]
          : [{type: "Contact", id: "LIST"}],
    }),

    // Get Contact by ID
    getContactById: builder.query<ContactResponseDto, number>({
      query: (id) => `/contacts/${id}`,
      providesTags: (result, error, id) => [{type: "Contact", id}],
    }),

    // Update Contact
    updateContact: builder.mutation<
      ContactResponseDto,
      {id: number} & UpdateContactDto
    >({
      query: ({id, ...data}) => ({
        url: `/contacts/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "Contact", id},
        {type: "Contact", id: "LIST"},
      ],
    }),

    // Update Contact Status
    updateContactStatus: builder.mutation<
      ContactResponseDto,
      {id: number; status: ContactStatus}
    >({
      query: ({id, status}) => ({
        url: `/contacts/${id}/status`,
        method: "PATCH",
        body: {status},
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "Contact", id},
        {type: "Contact", id: "LIST"},
      ],
    }),

    // Delete Contact
    deleteContact: builder.mutation<
      {success: boolean; message: string},
      number
    >({
      query: (id) => ({
        url: `/contacts/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        {type: "Contact", id},
        {type: "Contact", id: "LIST"},
      ],
    }),

    // Get Contact Stats
    getContactStats: builder.query<ContactStatsDto, void>({
      query: () => "/contacts/stats",
      providesTags: ["Contact"],
    }),

    // Bulk Update Contact Status
    bulkUpdateContactStatus: builder.mutation<
      BulkContactOperationResponseDto,
      BulkUpdateContactStatusDto
    >({
      query: (data) => ({
        url: "/contacts/bulk/status",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Contact"],
    }),

    // Bulk Delete Contacts
    bulkDeleteContacts: builder.mutation<
      BulkContactOperationResponseDto,
      {contactIds: number[]}
    >({
      query: (data) => ({
        url: "/contacts/bulk/delete",
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["Contact"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateContactMutation,
  useGetContactsQuery,
  useGetContactByIdQuery,
  useUpdateContactMutation,
  useUpdateContactStatusMutation,
  useDeleteContactMutation,
  useGetContactStatsQuery,
  useBulkUpdateContactStatusMutation,
  useBulkDeleteContactsMutation,
} = contactApi;
